import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import ClipLoader from "react-spinners/ClipLoader";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CheckIcon from "@mui/icons-material/Check";
import AddIcon from "@mui/icons-material/Add";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../All CSS/Settings.css";

const API = process.env.REACT_APP_API_BASE_URL;

function InsertFRMS() {
  const [loading, setLoading] = useState(false);
  const [formOptions, setFormOptions] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [selectedForm, setSelectedForm] = useState("");
  const [selectedDisplayValue, setSelectedDisplayValue] = useState("");
  const [hasLoadedGrid, setHasLoadedGrid] = useState(false);
  const [showNewFormPopup, setShowNewFormPopup] = useState(false);
  const [newFormText, setNewFormText] = useState("");
  const [newFormName, setNewFormName] = useState("");
  const [newObjectCode, setNewObjectCode] = useState("");
  const [newTableName, setNewTableName] = useState("");
  const [tableRows, setTableRows] = useState([]);
  const [pageSize, setPageSize] = useState(100);
  const selectRef = useRef(null);

  useEffect(() => {
    const loadFormOptions = async () => {
      try {
        setLoading(true);
        const res = await axios.post(`${API}/api/form-frms-list`);

        const rows = Array.isArray(res?.data?.data) ? res.data.data : [];
        setFormOptions(rows);
      } catch (error) {
        console.error("Form list load failed", error);
        toast.error("Failed to load form list");
      } finally {
        setLoading(false);
      }
    };

    loadFormOptions();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const filteredOptions = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();

    if (!query) {
      return formOptions;
    }

    return formOptions.filter((option) => {
      const formName = option?.FormName || "";
      const menuName = option?.MenuName || "";

      return (
        formName.toLowerCase().includes(query) ||
        menuName.toLowerCase().includes(query)
      );
    });
  }, [formOptions, searchTerm]);

  const handleSelect = (option) => {
    const formName = option.FormName || "";
    const menuName = option.MenuName || "";

    setSelectedForm(formName);
    setSelectedDisplayValue(
      menuName && formName ? `${menuName} - ${formName}` : formName,
    );
    setHasLoadedGrid(false);
    setSearchTerm("");
    setIsOpen(false);
  };

  const handleDisplay = async () => {
    if (!selectedForm) {
      toast.error("Please select a Form Name first");
      return;
    }

    setHasLoadedGrid(false);

    try {
      setLoading(true);

      const res = await axios.post(`${API}/api/form-frms-settings-details`, {
        FormName: selectedForm,
      });

      const details = Array.isArray(res?.data?.data) ? res.data.data : [];

      if (details.length > 0) {
        setTableRows(
          details.map((row, index) => ({
            ...row,
            id:
              row.id ??
              `${row.FormName ?? "row"}-${row.ObjectCode ?? "obj"}-${row.ColIndex ?? index}-${index}`,
          })),
        );
        setHasLoadedGrid(true);
        toast.success(`Displaying ${selectedForm}`);
      } else {
        setTableRows([]);
        setHasLoadedGrid(false);
        toast.info(`No settings found for ${selectedForm}`);
      }
    } catch (error) {
      console.error("Display failed", error);
      setHasLoadedGrid(false);
      toast.error("Unable to load selected form settings");
    } finally {
      setLoading(false);
    }
  };

  const normalizedTableRows = useMemo(
    () =>
      tableRows.map((row, index) => ({
        ...row,
        id:
          row.id ??
          `${row.FormName ?? "row"}-${row.ObjectCode ?? "obj"}-${row.ColIndex ?? index}-${index}`,
      })),
    [tableRows],
  );

  const tableColumns = useMemo(() => {
    if (!normalizedTableRows.length) {
      return [];
    }

    const fields = Object.keys(normalizedTableRows[0]).filter(
      (field) => field !== "id",
    );

    return fields.map((field) => ({
      field,
      headerName: field
        .replace(/([A-Z])/g, " $1")
        .replace(/^./, (char) => char.toUpperCase()),
      minWidth: field.toLowerCase().includes("query") ? 220 : 120,
      flex: field.toLowerCase().includes("query") ? 1.5 : 1,
      renderCell: (params) => {
        const value = params.value;
        const displayValue =
          value === null || value === undefined || value === "" ? "-" : value;

        return (
          <span className="insert-settings-grid-cell">{displayValue}</span>
        );
      },
    }));
  }, [normalizedTableRows]);


  const handleNewFormSubmit = async (event) => {
    event.preventDefault();

    const trimmedFormText = newFormText.trim();
    const trimmedFormName = newFormName.trim();
    const trimmedObjectCode = newObjectCode.trim();
    const trimmedTableName = newTableName.trim();

    // ============================================
    // Validation
    // ============================================

    if (!selectedForm) {
      toast.error("Please select an existing Form Name first");
      return;
    }

    if (!trimmedFormText) {
      toast.error("Please enter Form Text");
      return;
    }

    if (!trimmedFormName) {
      toast.error("Please enter FormName");
      return;
    }

    if (!trimmedObjectCode) {
      toast.error("Please enter ObjectCode");
      return;
    }

    if (!trimmedTableName) {
      toast.error("Please enter Table Name");
      return;
    }

    if (trimmedFormName.toLowerCase() === selectedForm.toLowerCase()) {
      toast.error("New FormName must be different from the existing FormName");
      return;
    }

    try {
      setLoading(true);

      // ============================================
      // Call Backend
      // ============================================

      const res = await axios.post(`${API}/api/form-frms-settings-new-form`, {
        SourceFormName: selectedForm,
        NewFormText: trimmedFormText,
        NewFormName: trimmedFormName,
        NewObjectCode: trimmedObjectCode,
        NewTableName: trimmedTableName,
      });

      // ============================================
      // Check Response
      // ============================================

      if (!res?.data?.success) {
        toast.error(res?.data?.message || "Failed to create new form");

        return;
      }

      const rowsInserted = res?.data?.data?.rowsInserted || 0;

      // ============================================
      // Add New Form to Dropdown
      // ============================================

      setFormOptions((prev) => {
        const alreadyExists = prev.some(
          (option) =>
            String(option.FormName || "").toLowerCase() ===
            trimmedFormName.toLowerCase(),
        );

        if (alreadyExists) {
          return prev;
        }

        return [
          ...prev,
          {
            MenuName: trimmedFormText,
            FormName: trimmedFormName,
          },
        ];
      });

      // ============================================
      // Select New Form
      // ============================================

      setSelectedForm(trimmedFormName);

      setSelectedDisplayValue(`${trimmedFormText} - ${trimmedFormName}`);

      // ============================================
      // Close Popup
      // ============================================

      setShowNewFormPopup(false);

      // ============================================
      // Clear Popup Fields
      // ============================================

      setNewFormText("");
      setNewFormName("");
      setNewObjectCode("");
      setNewTableName("");

      // ============================================
      // Success Message
      // ============================================

      toast.success(`${rowsInserted} rows inserted successfully`);

      // ============================================
      // Automatically Display New Form
      // ============================================

      const detailsRes = await axios.post(
        `${API}/api/form-frms-settings-details`,
        {
          FormName: trimmedFormName,
        },
      );

      const details = Array.isArray(detailsRes?.data?.data)
        ? detailsRes.data.data
        : [];

      setTableRows(
        details.map((row, index) => ({
          ...row,
          id:
            row.id ??
            `${row.FormName ?? "row"}-${row.ObjectCode ?? "obj"}-${row.ColIndex ?? index}-${index}`,
        })),
      );
    } catch (error) {
      console.error("Create new form failed:", error);

      const message =
        error?.response?.data?.message || "Unable to create new form";

      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="insert-settings-page">
      <ToastContainer
        position="top-center"
        autoClose={4000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnHover
      />

      <div className="insert-settings-shell">
        {loading && (
          <div className="insert-settings-loading-overlay">
            <div className="insert-settings-loading-box">
              <ClipLoader size={32} color="#2563eb" />
              <p className="insert-settings-loading-text">
                Loading, please wait...
              </p>
            </div>
          </div>
        )}

        <div className="insert-settings-header">
          <div className="insert-settings-field-wrap" ref={selectRef}>
            <div
              className={`insert-settings-select-box ${isOpen ? "open" : ""}`}
              onClick={() => setIsOpen((prev) => !prev)}
              role="button"
              tabIndex={0}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  setIsOpen((prev) => !prev);
                }
              }}
            >
              <div className="insert-settings-selected-value">
                {selectedDisplayValue ? (
                  <span>{selectedDisplayValue}</span>
                ) : (
                  <span className="insert-settings-placeholder">
                    Select Form Name
                  </span>
                )}
              </div>

              <button
                type="button"
                className="insert-settings-select-toggle"
                aria-label="Toggle form list"
                onClick={(event) => {
                  event.stopPropagation();
                  setIsOpen((prev) => !prev);
                }}
              >
                <KeyboardArrowDownIcon />
              </button>
            </div>

            {isOpen && (
              <div className="insert-settings-dropdown">
                <div className="insert-settings-search-box">
                  <SearchIcon className="insert-settings-search-icon" />
                  <input
                    type="text"
                    placeholder="Search form or menu..."
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    className="insert-settings-search-input"
                  />
                </div>

                <div className="insert-settings-options">
                  {filteredOptions.length > 0 ? (
                    filteredOptions.map((option, index) => {
                      const isSelected = option.FormName === selectedForm;

                      return (
                        <div
                          key={`${option.FormName}-${index}`}
                          className={`insert-settings-option ${isSelected ? "selected" : ""}`}
                          onClick={() => handleSelect(option)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              handleSelect(option);
                            }
                          }}
                        >
                          <div className="insert-settings-option-main">
                            <span className="insert-settings-option-name">
                              {option.FormName}
                            </span>
                            <span className="insert-settings-option-menu">
                              {option.MenuName}
                            </span>
                          </div>

                          {isSelected && (
                            <CheckIcon className="insert-settings-option-check" />
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <div className="insert-settings-no-results">
                      No matching form found
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <button
            type="button"
            className="insert-settings-display-btn"
            onClick={handleDisplay}
          >
            {hasLoadedGrid ? (
              <VisibilityIcon className="insert-settings-display-icon" />
            ) : (
              <VisibilityOffIcon className="insert-settings-display-icon" />
            )}
            Display
          </button>
        </div>

        {normalizedTableRows.length > 0 && (
          <div className="insert-settings-grid-card">
            <div className="insert-settings-grid-toolbar">
              <button
                type="button"
                className="insert-settings-new-form-btn"
                onClick={() => setShowNewFormPopup(true)}
              >
                <AddIcon className="insert-settings-new-form-icon" />
                New Form
              </button>
            </div>

            <div className="insert-settings-grid-wrapper">
              <DataGrid
                rows={normalizedTableRows}
                columns={tableColumns}
                getRowId={(row) => row.id}
                disableSelectionOnClick
                disableColumnFilter={false}
                density="compact"
                rowHeight={42}
                headerHeight={32}
                pagination
                pageSize={pageSize}
                rowsPerPageOptions={[100, 150, 200, 300]}
                onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                className="insert-settings-data-grid"
              />
            </div>

          </div>
        )}
      </div>

      {showNewFormPopup && (
        <div
          className="insert-settings-modal-overlay"
          onClick={() => setShowNewFormPopup(false)}
        >
          <div
            className="insert-settings-modal"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="insert-settings-modal-header">
              <h3 className="insert-settings-modal-title">New Form</h3>
              <button
                type="button"
                className="insert-settings-modal-close"
                onClick={() => setShowNewFormPopup(false)}
                aria-label="Close new form popup"
              >
                ×
              </button>
            </div>

            <form
              className="insert-settings-modal-form"
              onSubmit={handleNewFormSubmit}
            >
              <div className="insert-settings-modal-field">
                <label htmlFor="insert-settings-form-text">Form Text</label>
                <input
                  id="insert-settings-form-text"
                  type="text"
                  value={newFormText}
                  onChange={(event) => setNewFormText(event.target.value)}
                  placeholder="Enter form text"
                />
              </div>

              <div className="insert-settings-modal-field">
                <label htmlFor="insert-settings-form-name">FormName</label>
                <input
                  id="insert-settings-form-name"
                  type="text"
                  value={newFormName}
                  onChange={(event) => setNewFormName(event.target.value)}
                  placeholder="Enter form name"
                />
              </div>

              <div className="insert-settings-modal-field">
                <label htmlFor="insert-settings-object-code">ObjectCode</label>
                <input
                  id="insert-settings-object-code"
                  type="text"
                  value={newObjectCode}
                  onChange={(event) => setNewObjectCode(event.target.value)}
                  placeholder="Enter object code"
                />
              </div>

              <div className="insert-settings-modal-field">
                <label htmlFor="insert-settings-table-name">Table Name</label>
                <input
                  id="insert-settings-table-name"
                  type="text"
                  value={newTableName}
                  onChange={(event) => setNewTableName(event.target.value)}
                  placeholder="Enter table name"
                />
              </div>

              <button type="submit" className="insert-settings-modal-submit">
                Submit
              </button>
            </form>
          </div>
        </div>
      )}
    </main>
  );
}

export default InsertFRMS;
